import { makeAutoObservable, reaction } from 'mobx';
import joinUrl from 'url-join';
import globalConfig from '../js/config';
import {
  generateUrlWithRegion,
  updateQueryStringParameters
} from '../js/url-utils';
import { AnalyticsStore } from './AnalyticsStore';
import { FormatState, FormatStateStore } from './FormatStateStore';
import { MraidStore } from './MraidStore';
import { DebugLog } from '../js/debug-log';

interface MonitoringStoreProps {
  mraidStore: MraidStore;
  formatStateStore: FormatStateStore;
  analyticsStore: AnalyticsStore;
  formatContext: AdContext;
  formatConfig: Record<string, any>;
}

export class MonitoringStore {
  private readonly _mraidStore: MraidStore;
  private readonly _formatStateStore: FormatStateStore;
  private readonly _analyticsStore: AnalyticsStore;
  private readonly _formatContext: AdContext;
  private readonly _formatConfig: Record<string, any>;

  public get isEnabled() {
    return !!this._formatConfig.monitoringEnabled;
  }

  constructor({
    mraidStore,
    formatStateStore,
    analyticsStore,
    formatContext,
    formatConfig
  }: MonitoringStoreProps) {
    this._mraidStore = mraidStore;
    this._formatStateStore = formatStateStore;
    this._analyticsStore = analyticsStore;
    this._formatContext = formatContext;
    this._formatConfig = formatConfig;

    makeAutoObservable(this);

    this._initialize();
  }

  public sendLog(message: string) {
    if (!this._formatConfig.monitoringEnabled) return;

    const formatContext = {
      ...this._formatContext,
      userId: undefined,
      userDataOptin: undefined
    };

    const pixel = new Image();
    const url = updateQueryStringParameters(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.MS_ADS_MONITORING_EVENTS_URL,
          this._formatContext.region
        ),
        'monitoring'
      ),
      {
        msg: message,
        timestamp: new Date().toISOString(),
        viewable: this._mraidStore.isViewable,
        formatState: FormatState[this._formatStateStore.state],
        impressionSent: this._analyticsStore.impressionFormatSent,
        ...formatContext,
        sdkVersion: window.MRAID_ENV?.sdkVersion || undefined
      }
    );
    pixel.src = url;
  }

  private _initialize() {
    if (!this._formatConfig.monitoringEnabled) return;

    reaction(
      () => this._mraidStore.isViewable,
      () => {
        DebugLog.log('viewability changed', {
          isViewable: this._mraidStore.isViewable
        });
        this.sendLog('viewability changed');
      }
    );

    reaction(
      () => this._formatStateStore.state,
      () => {
        DebugLog.log('format state changed', {
          state: FormatState[this._formatStateStore.state]
        });
        this.sendLog('format state changed');
      }
    );

    reaction(
      () => this._analyticsStore.impressionFormatSent,
      () => {
        DebugLog.log('impression has been sent');
        this.sendLog('impression sent');
      }
    );
  }
}
